import { useState, useMemo } from 'react';
import { ChevronDown, ChevronUp, PlayCircle, X } from 'lucide-react';

/**
 * FeatureVideoHero — compact, above-the-fold video panel placed directly below
 * a page title on every analysis screen (Snapshot, Spreadsheet Growth
 * Comparison, Growth vs Model, Multi-Profile).
 *
 * Design rules:
 *   • Visible without scrolling — rendered AS the first child under the page title
 *   • Starts EXPANDED by default so users see the video immediately
 *   • User can collapse / re-expand with a single click (preference held for
 *     the session only — we intentionally do not persist, since this is a
 *     marketing/orientation touchpoint, not a settings item)
 *   • Accepts any YouTube URL form — `watch?v=…`, `youtu.be/…`, or `/embed/…`
 *     — and normalises to the embed form client-side
 */
export default function FeatureVideoHero({
  featureName = 'this feature',
  youtubeUrl,
  defaultOpen = true,
}) {
  const [open, setOpen] = useState(defaultOpen);
  const [closed, setClosed] = useState(false);

  const embedUrl = useMemo(() => {
    if (!youtubeUrl) return null;
    const s = String(youtubeUrl).trim();
    let m = s.match(/^https?:\/\/youtu\.be\/([A-Za-z0-9_-]{6,})/);
    if (m) return `https://www.youtube.com/embed/${m[1]}`;
    m = s.match(/^https?:\/\/(?:www\.)?youtube\.com\/watch\?(?:.*&)?v=([A-Za-z0-9_-]{6,})/);
    if (m) return `https://www.youtube.com/embed/${m[1]}`;
    if (s.includes('/embed/')) return s;
    return null;
  }, [youtubeUrl]);

  if (closed || !embedUrl) return null;

  return (
    <div
      className="mb-4 rounded-xl border border-slate-200 bg-gradient-to-r from-slate-50 to-white shadow-sm overflow-hidden"
      data-testid="feature-video-hero"
    >
      {/* Header strip */}
      <div className="flex items-center justify-between px-4 py-2.5 border-b border-slate-200 bg-white/60">
        <div className="flex items-center gap-2 min-w-0">
          <PlayCircle className="h-4 w-4 text-[#0891b2] shrink-0" />
          <span className="text-sm font-medium text-slate-800 truncate">
            Quick tour: {featureName}
          </span>
        </div>
        <div className="flex items-center gap-1.5 shrink-0">
          <button
            onClick={() => setOpen((v) => !v)}
            className="p-1.5 rounded hover:bg-slate-100 transition-colors"
            data-testid="feature-video-toggle"
            title={open ? 'Collapse video' : 'Expand video'}
            aria-label={open ? 'Collapse video' : 'Expand video'}
          >
            {open ? (
              <ChevronUp className="h-4 w-4 text-slate-500" />
            ) : (
              <ChevronDown className="h-4 w-4 text-slate-500" />
            )}
          </button>
          <button
            onClick={() => setClosed(true)}
            className="p-1.5 rounded hover:bg-slate-100 transition-colors"
            data-testid="feature-video-dismiss"
            title="Dismiss"
            aria-label="Dismiss video"
          >
            <X className="h-4 w-4 text-slate-500" />
          </button>
        </div>
      </div>

      {/* Embedded iframe — hidden when collapsed. max-w keeps it sane on wide
          screens while preserving 16:9. */}
      {open && (
        <div className="p-3 sm:p-4">
          <div
            className="relative w-full max-w-3xl mx-auto rounded-lg overflow-hidden bg-black shadow"
            style={{ aspectRatio: '16 / 9' }}
          >
            <iframe
              src={embedUrl}
              title={`${featureName} — quick tour`}
              allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
              allowFullScreen
              className="absolute inset-0 w-full h-full"
              data-testid="feature-video-iframe"
            />
          </div>
        </div>
      )}
    </div>
  );
}
